import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/shared/PageHero';
import { EmptyState } from '@/components/shared/EmptyState';
import { ANNOUNCEMENTS } from '@/data/mockData';
import { formatDate } from '@/lib/storage';
import { ChevronRight, Pin, Search, Megaphone, Building2 } from 'lucide-react';

const CATEGORIES = ['All', ...Array.from(new Set(ANNOUNCEMENTS.map((a) => a.category)))];

const Announcements = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  const list = useMemo(() => {
    return ANNOUNCEMENTS.filter((a) => {
      const matchCat = cat === 'All' || a.category === cat;
      const matchQ = (a.title + a.summary).toLowerCase().includes(q.toLowerCase());
      return matchCat && matchQ;
    });
  }, [q, cat]);

  return (
    <div data-testid="announcements-page">
      <PageHero
        eyebrow="News & Updates"
        title="Announcements & Advisories"
        subtitle="Official barangay communications, public hearings, advisories, and program announcements."
        breadcrumbs={[{ label: 'Announcements' }]}
      />

      <section className="container-app section-padding">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search announcements..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="announcements-search"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-thin -mx-1 px-1">
            {CATEGORIES.map((c) => (
              <Button
                key={c}
                size="sm"
                variant={cat === c ? 'default' : 'outline'}
                onClick={() => setCat(c)}
                className={cat === c ? 'bg-primary text-primary-foreground' : ''}
                data-testid={`announcements-filter-${c.replace(/\s|\//g, '-').toLowerCase()}`}
              >
                {c}
              </Button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <EmptyState icon={Megaphone} title="No announcements found" description="Try a different keyword or category filter." />
        ) : (
          <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-5">
            {list.map((a) => (
              <Link key={a.id} to={`/announcements/${a.id}`} className="group block" data-testid={`announcement-${a.id}`}>
                <Card className="h-full overflow-hidden card-elevated border-0">
                  {a.image ? (
                    <div className="aspect-[16/9] overflow-hidden bg-secondary">
                      <img src={a.image} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition duration-700" />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-primary/15 to-accent-soft flex items-center justify-center">
                      <Building2 className="h-10 w-10 text-primary/40" />
                    </div>
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20">{a.category}</Badge>
                      {a.pinned && <Badge className="bg-warning/15 text-warning hover:bg-warning/25 border-warning/30 gap-1"><Pin className="h-3 w-3" /> Pinned</Badge>}
                    </div>
                    <h3 className="mt-3 font-heading font-semibold leading-snug line-clamp-2">{a.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{a.summary}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-[11px] text-muted-foreground">{formatDate(a.date)}</span>
                      <span className="inline-flex items-center text-xs font-semibold text-primary group-hover:gap-1.5 gap-1 transition-all">
                        Read more <ChevronRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Announcements;
